import { useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function PaymentsPanel({ payments }) {
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPerPage, setPaymentsPerPage] = useState(25);
  const [paymentFilters, setPaymentFilters] = useState({
    search: '',
    plan: '',
    status: ''
  });

  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      const matchesSearch = !paymentFilters.search ||
        payment.user_email?.toLowerCase().includes(paymentFilters.search.toLowerCase()) ||
        payment.user_name?.toLowerCase().includes(paymentFilters.search.toLowerCase()) ||
        payment.transaction_id?.toLowerCase().includes(paymentFilters.search.toLowerCase());
      
      const matchesPlan = !paymentFilters.plan || payment.plan_id === paymentFilters.plan;
      const matchesStatus = !paymentFilters.status || payment.payment_status === paymentFilters.status;
      
      return matchesSearch && matchesPlan && matchesStatus;
    });
  }, [payments, paymentFilters]);

  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);
  const startIndex = (paymentsPage - 1) * paymentsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, startIndex + paymentsPerPage);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Payment Transactions</h2>
          <p className="text-slate-500 text-sm mt-1">
            {filteredPayments.length} transaction{filteredPayments.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-slate-600">Per page:</Label>
          <select
            value={paymentsPerPage}
            onChange={(e) => {
              setPaymentsPerPage(Number(e.target.value));
              setPaymentsPage(1);
            }}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm"
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={paymentFilters.search}
            onChange={(e) => setPaymentFilters({ ...paymentFilters, search: e.target.value })}
            placeholder="Search by user, email, or transaction ID..."
            className="pl-10"
          />
        </div>
        <select
          value={paymentFilters.plan}
          onChange={(e) => setPaymentFilters({ ...paymentFilters, plan: e.target.value })}
          className="h-10 rounded-lg border border-slate-200 px-3 text-sm"
        >
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
          <option value="crm">CRM</option>
        </select>
        <select
          value={paymentFilters.status}
          onChange={(e) => setPaymentFilters({ ...paymentFilters, status: e.target.value })}
          className="h-10 rounded-lg border border-slate-200 px-3 text-sm"
        >
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        {(paymentFilters.search || paymentFilters.plan || paymentFilters.status) && (
          <Button
            variant="ghost"
            onClick={() => setPaymentFilters({ search: '', plan: '', status: '' })}
          >
            Reset
          </Button>
        )}
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">User</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Plan</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Amount</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Status</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Date</th>
              <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700">Transaction ID</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {paginatedPayments.map(payment => (
              <tr key={payment.transaction_id} className="hover:bg-slate-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-slate-900">{payment.user_name || 'Unknown'}</p>
                    <p className="text-sm text-slate-500">{payment.user_email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-slate-700 capitalize">{payment.plan_id}</span>
                  <span className="text-xs text-slate-500 ml-2">({payment.billing_cycle})</span>
                </td>
                <td className="px-6 py-4 font-semibold text-slate-900">
                  ${payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    payment.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                    payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {payment.payment_status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {new Date(payment.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">
                  {payment.transaction_id}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-600">Page {paymentsPage} of {totalPages}</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaymentsPage(p => Math.max(1, p - 1))}
              disabled={paymentsPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaymentsPage(p => Math.min(totalPages, p + 1))}
              disabled={paymentsPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
